// ============================================================
// deletionLog.js
// A record of what was deleted, by whom, and when — for the whole app, not for
// any one screen.
//
// WHY THE SNAPSHOT IS THE POINT.
// A log saying "SD-26-0007 was deleted by STEVE" answers who. It does not
// answer the question anybody actually asks two weeks later, which is what was
// in it. So the deleted document's fields are stored alongside the entry. It is
// not a restore feature and must not become one — deletion here is permanent by
// design — but it is the difference between knowing something is missing and
// knowing what is missing.
//
// GENERAL, NOT DRAWING-SHAPED. Shop drawings are the first caller. Invoices,
// customers and products are the obvious next ones, and none of them should
// have to invent a field layout. `entityType` is a free string precisely so a
// new caller needs no change here; `label` and `detail` are whatever reads
// sensibly for that kind of record.
//
// ONE WRITER. Everything goes through logDeletion(). A second function writing
// "nearly the same" shape is how a log ends up with three field names for the
// same idea and no way to query across them.
//
// ⚠️ THIS IS NOT ENFORCED. Firestore rules currently let any authenticated user
// write anything, so nothing stops a determined person writing a log entry by
// hand, or deleting one. The log is a record for honest people and an audit
// trail for accidents; it becomes a control when the rules step lands.
// ============================================================

import { collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase';

// How much of the snapshot to keep, in characters of JSON.
//
// A Firestore document is capped at 1 MiB in total, and this entry carries its
// own fields on top of the snapshot. 200 KB is roughly a fifth of the budget:
// far more than any record in this app (the largest, an invoice with a hundred
// line items and a pickup signature, is a few tens of KB), and small enough
// that a pathological document cannot take the log entry down with it.
export const MAX_SNAPSHOT_CHARS = 200000;

/** Firestore Timestamp → ISO string; everything else through unchanged. */
const toIso = (v) => {
  if (v && typeof v.toDate === 'function') {
    const d = v.toDate();
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
  }
  return v;
};

/**
 * Make an arbitrary document safe to embed.
 *
 * Firestore REJECTS a write containing `undefined` anywhere, so a single absent
 * field would fail the log write and — because the log is written first — abort
 * a delete the user asked for. Dropping undefined is not tidiness here, it is
 * the difference between the feature working and not.
 *
 * Timestamps become ISO strings, matching the rest of the app's stored dates
 * and making the snapshot readable as plain JSON afterwards.
 */
function sanitise(value, depth = 0) {
  if (value === null || value === undefined) return null;
  if (depth > 12) return '[nested too deep]';   // guards a cyclic structure
  const converted = toIso(value);
  if (converted !== value) return converted;
  if (Array.isArray(value)) return value.map(v => sanitise(v, depth + 1));
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).forEach(k => {
      if (value[k] === undefined) return;       // see the note above
      out[k] = sanitise(value[k], depth + 1);
    });
    return out;
  }
  if (typeof value === 'function') return null;
  return value;
}

const sizeOf = (obj) => {
  try { return JSON.stringify(obj).length; } catch { return Infinity; }
};

/**
 * Trim a snapshot down to something that will fit.
 *
 * Drops the BIGGEST fields first and names every one it dropped, so the entry
 * says what it is missing rather than quietly being incomplete. A truncated
 * snapshot is far better than a failed delete: the user asked for the document
 * to go, and refusing because it was too fat to photograph would be absurd.
 *
 * Returns { snapshot, omitted, truncated }.
 */
function fitSnapshot(clean) {
  if (sizeOf(clean) <= MAX_SNAPSHOT_CHARS) {
    return { snapshot: clean, omitted: [], truncated: false };
  }
  const kept = { ...clean };
  const omitted = [];
  // Largest first, so the fewest fields are lost.
  const bySize = Object.keys(kept)
    .map(k => ({ k, size: sizeOf(kept[k]) }))
    .sort((a, b) => b.size - a.size);
  for (const { k } of bySize) {
    if (sizeOf(kept) <= MAX_SNAPSHOT_CHARS) break;
    delete kept[k];
    omitted.push(k);
  }
  return { snapshot: kept, omitted, truncated: true };
}

/**
 * Record a deletion.
 *
 * ⚠️ CALL THIS BEFORE DELETING, AND LET IT THROW.
 * The caller must write the log entry first and only delete if it succeeded. A
 * delete with no record is the exact thing this module exists to prevent, and
 * it is the order — not the code here — that guarantees it. This function
 * therefore does NOT swallow its errors: it rejects, and the caller aborts.
 *
 * Returns the new log entry's id.
 */
export async function logDeletion({
  entityType, entityId, label, detail, snapshot, deletedBy,
}) {
  const type = String(entityType || '').trim();
  if (!type) throw new Error('logDeletion: entityType is required');

  const clean = sanitise(snapshot || {});
  const { snapshot: fitted, omitted, truncated } = fitSnapshot(
    clean && typeof clean === 'object' && !Array.isArray(clean) ? clean : { value: clean },
  );

  const entry = {
    entityType: type,
    entityId: String(entityId || ''),
    label: String(label || ''),
    detail: String(detail || ''),
    snapshot: fitted,
    // Named so the omission is visible on the entry itself rather than being
    // something you could only notice by knowing what should have been there.
    snapshotTruncated: truncated,
    snapshotOmittedFields: omitted,
    deletedBy: String(deletedBy || ''),
    // ISO string, like every other date this app stores.
    deletedAt: new Date().toISOString(),
  };

  const ref = await addDoc(collection(db, 'deletionLog'), entry);
  return ref.id;
}
